import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Eye, Pencil, Trash2, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";
import { VisitationSessionForm } from "./VisitationSessionForm";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VisitationCaseRecord {
  id: number;
  person_id: number;
  agency_id: number;
  is_custodial_parent: number;
  other_parent_first_name: string | null;
  other_parent_last_name: string | null;
  other_parent_phone: string | null;
  other_parent_address: string | null;
  family_issues: string | null;
  created_at: string;
}

interface VisitationSessionRecord {
  id: number;
  visitation_case_id: number;
  session_type: string;
  start_date: string;
  start_time: string | null;
  end_date: string | null;
  end_time: string | null;
  duration_minutes: number | null;
  details: string | null;
  terminated: number;
  terminated_reason: string | null;
  created_at: string;
}

interface VisitationChildRecord {
  id: number;
  visitation_case_id: number;
  child_person_id: number | null;
  child_name: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface VisitationCasesProps {
  personId: string;
}

export function VisitationCases({ personId }: VisitationCasesProps) {
  const queryClient = useQueryClient();
  const [showCaseForm, setShowCaseForm] = useState(false);
  const [editingCase, setEditingCase] = useState<VisitationCaseRecord | null>(null);
  const [expandedCaseId, setExpandedCaseId] = useState<number | null>(null);
  const [showSessionForm, setShowSessionForm] = useState<number | null>(null);

  const { data, isLoading } = useQuery<{ data: VisitationCaseRecord[] }>({
    queryKey: ["visitationCases", personId],
    queryFn: () =>
      api.get<{ data: VisitationCaseRecord[] }>(
        `/clients/${personId}/visitation-cases`,
      ),
    enabled: !!personId,
  });

  const createCaseMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post(`/clients/${personId}/visitation-cases`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["visitationCases", personId] });
      setShowCaseForm(false);
    },
  });

  const updateCaseMutation = useMutation({
    mutationFn: ({ id, ...body }: { id: number } & Record<string, unknown>) =>
      api.put(`/clients/${personId}/visitation-cases/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["visitationCases", personId] });
      setEditingCase(null);
    },
  });

  const deleteCaseMutation = useMutation({
    mutationFn: (id: number) =>
      api.delete(`/clients/${personId}/visitation-cases/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["visitationCases", personId] });
    },
  });

  const cases = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Visitation Cases</h3>
        <Button size="sm" className="gap-2" onClick={() => setShowCaseForm(true)}>
          <Plus className="h-4 w-4" />
          Add Case
        </Button>
      </div>

      {showCaseForm && (
        <Card>
          <CardContent className="p-4">
            <VisitationCaseForm
              onSubmit={(d) => createCaseMutation.mutate(d)}
              onCancel={() => setShowCaseForm(false)}
              isLoading={createCaseMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {editingCase && (
        <Card>
          <CardContent className="p-4">
            <VisitationCaseForm
              initial={editingCase}
              onSubmit={(d) =>
                updateCaseMutation.mutate({ id: editingCase.id, ...d })
              }
              onCancel={() => setEditingCase(null)}
              isLoading={updateCaseMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {!isLoading && cases.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No visitation cases found.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading &&
        cases.map((vc) => {
          const otherParent =
            vc.other_parent_first_name || vc.other_parent_last_name
              ? `${vc.other_parent_first_name ?? ""} ${vc.other_parent_last_name ?? ""}`.trim()
              : null;
          const isExpanded = expandedCaseId === vc.id;

          return (
            <Card key={vc.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm font-medium">
                      Visitation Case #{vc.id}
                    </CardTitle>
                    <CardDescription className="mt-0.5">
                      {vc.is_custodial_parent ? "Custodial Parent" : "Non-Custodial Parent"}
                      {otherParent && ` | Other Parent: ${otherParent}`}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        setExpandedCaseId(isExpanded ? null : vc.id)
                      }
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditingCase(vc)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (confirm("Delete this visitation case and all its sessions?")) {
                          deleteCaseMutation.mutate(vc.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {vc.family_issues && (
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">Family Issues:</span> {vc.family_issues}
                  </p>
                )}
                {vc.other_parent_phone && (
                  <p className="text-xs text-muted-foreground mt-1">
                    <span className="font-medium">Other Parent Phone:</span> {vc.other_parent_phone}
                  </p>
                )}

                {isExpanded && (
                  <VisitationSessionsPanel
                    caseId={vc.id}
                    showSessionForm={showSessionForm === vc.id}
                    onToggleSessionForm={() =>
                      setShowSessionForm(showSessionForm === vc.id ? null : vc.id)
                    }
                  />
                )}
              </CardContent>
            </Card>
          );
        })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sessions Panel (shown when a case is expanded)
// ---------------------------------------------------------------------------

function VisitationSessionsPanel({
  caseId,
  showSessionForm,
  onToggleSessionForm,
}: {
  caseId: number;
  showSessionForm: boolean;
  onToggleSessionForm: () => void;
}) {
  const queryClient = useQueryClient();

  const { data: sessionsData, isLoading: sessionsLoading } = useQuery<{
    data: VisitationSessionRecord[];
  }>({
    queryKey: ["visitationSessions", caseId],
    queryFn: () =>
      api.get<{ data: VisitationSessionRecord[] }>(
        `/visitation-cases/${caseId}/sessions`,
      ),
  });

  const { data: childrenData } = useQuery<{ data: VisitationChildRecord[] }>({
    queryKey: ["visitationChildren", caseId],
    queryFn: () =>
      api.get<{ data: VisitationChildRecord[] }>(
        `/visitation-cases/${caseId}/children`,
      ),
  });

  const createSessionMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post(`/visitation-cases/${caseId}/sessions`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["visitationSessions", caseId] });
      onToggleSessionForm();
    },
  });

  const deleteSessionMutation = useMutation({
    mutationFn: (id: number) =>
      api.delete(`/visitation-cases/${caseId}/sessions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["visitationSessions", caseId] });
    },
  });

  const sessions = sessionsData?.data ?? [];
  const children = childrenData?.data ?? [];

  const sessionTypeLabels: Record<string, string> = {
    visit: "Visit",
    supervised: "Supervised Visit",
    exchange: "Exchange",
    phone: "Phone",
    other: "Other",
  };

  return (
    <div className="mt-4 border-t pt-4 space-y-3">
      {/* Children */}
      {children.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Children:</p>
          <div className="flex flex-wrap gap-2">
            {children.map((ch) => (
              <span
                key={ch.id}
                className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700"
              >
                {ch.child_name ?? `Person #${ch.child_person_id}`}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Sessions header */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium">Sessions</p>
        <Button size="sm" variant="outline" className="gap-1 h-7 text-xs" onClick={onToggleSessionForm}>
          <Plus className="h-3 w-3" />
          Add Session
        </Button>
      </div>

      {showSessionForm && (
        <VisitationSessionForm
          onSubmit={(d) => createSessionMutation.mutate(d)}
          onCancel={onToggleSessionForm}
          isLoading={createSessionMutation.isPending}
        />
      )}

      {sessionsLoading && (
        <div className="h-12 animate-pulse rounded border bg-muted" />
      )}

      {!sessionsLoading && sessions.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-2">
          No sessions recorded yet.
        </p>
      )}

      {!sessionsLoading &&
        sessions.map((s) => (
          <div
            key={s.id}
            className="flex items-center justify-between rounded-md border p-2 text-xs"
          >
            <div>
              <span className="font-medium">
                {new Date(s.start_date).toLocaleDateString()}
              </span>
              {s.start_time && <span> at {s.start_time}</span>}
              <span className="ml-2 text-muted-foreground">
                {sessionTypeLabels[s.session_type] ?? s.session_type}
              </span>
              {s.duration_minutes && (
                <span className="ml-2 text-muted-foreground">
                  ({s.duration_minutes} min)
                </span>
              )}
              {s.terminated === 1 && (
                <span className="ml-2 text-red-600 font-medium">Terminated</span>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => {
                if (confirm("Delete this session?")) {
                  deleteSessionMutation.mutate(s.id);
                }
              }}
            >
              <Trash2 className="h-3 w-3 text-destructive" />
            </Button>
          </div>
        ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Case Form
// ---------------------------------------------------------------------------

function VisitationCaseForm({
  initial,
  onSubmit,
  onCancel,
  isLoading,
}: {
  initial?: VisitationCaseRecord;
  onSubmit: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [isCustodialParent, setIsCustodialParent] = useState(
    initial?.is_custodial_parent === 1 || initial?.is_custodial_parent === undefined,
  );
  const [otherParentFirstName, setOtherParentFirstName] = useState(
    initial?.other_parent_first_name ?? "",
  );
  const [otherParentLastName, setOtherParentLastName] = useState(
    initial?.other_parent_last_name ?? "",
  );
  const [otherParentPhone, setOtherParentPhone] = useState(
    initial?.other_parent_phone ?? "",
  );
  const [otherParentAddress, setOtherParentAddress] = useState(
    initial?.other_parent_address ?? "",
  );
  const [familyIssues, setFamilyIssues] = useState(initial?.family_issues ?? "");

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-foreground">
          Custodial Parent?
        </label>
        <input
          type="checkbox"
          checked={isCustodialParent}
          onChange={(e) => setIsCustodialParent(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input
          label="Other Parent First Name"
          value={otherParentFirstName}
          onChange={(e) => setOtherParentFirstName(e.target.value)}
        />
        <Input
          label="Other Parent Last Name"
          value={otherParentLastName}
          onChange={(e) => setOtherParentLastName(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input
          label="Other Parent Phone"
          value={otherParentPhone}
          onChange={(e) => setOtherParentPhone(e.target.value)}
        />
        <Input
          label="Other Parent Address"
          value={otherParentAddress}
          onChange={(e) => setOtherParentAddress(e.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">
          Family Issues
        </label>
        <textarea
          value={familyIssues}
          onChange={(e) => setFamilyIssues(e.target.value)}
          rows={3}
          placeholder="Describe family issues..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div className="flex gap-3">
        <Button
          size="sm"
          onClick={() =>
            onSubmit({
              isCustodialParent,
              otherParentFirstName: otherParentFirstName || undefined,
              otherParentLastName: otherParentLastName || undefined,
              otherParentPhone: otherParentPhone || undefined,
              otherParentAddress: otherParentAddress || undefined,
              familyIssues: familyIssues || undefined,
            })
          }
          loading={isLoading}
        >
          {initial ? "Update Case" : "Create Case"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
